"use client";
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/ui/dropdown-menu";
import {
  MoreVertical,
  FileDown,
  Edit,
  Info,
  CheckCircle,
} from "lucide-react";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { Estimate } from "../types/estimate";
import { formatIndiaDateTime } from "../../../utils/dateFormat";
import { Link } from "react-router-dom";
import { useOverlayHistory } from "../../../hooks/useOverlayHistory";
import { PriceSummaryReadOnly } from "../../../components/PriceSummaryReadOnly";
import {
  OrderProductsDetailsSection,
  coerceLineProducts,
} from "../orders/OrderProductsDetailsSection";

interface Props {
  estimate: Estimate;
  role?: "admin" | "dealer" | "employee";
  refresh: () => void;
  onEdit: (est: Estimate) => void;
  onConvert: (est: Estimate) => void;
}

const formatMoney = (value: string | undefined) => {
  const n = parseFloat(String(value ?? "0").replace(/[^\d.-]/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const EstimateCard: React.FC<Props> = ({
  estimate,
  role = "admin",
  refresh,
  onEdit,
  onConvert,
}) => {
  const { authToken = "", id = "" } = useSelector((state: any) => state.loginData || {});
  const [showDetails, setShowDetails] = useState<Estimate | null>(null);
  const converted = Boolean(estimate.convertedOrderId);

  useOverlayHistory(!!showDetails, () => setShowDetails(null), `estimate-details-${estimate.id}`);

  useEffect(() => {
    if (!showDetails || showDetails.id !== estimate.id) return;
    setShowDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        convertedOrderId: estimate.convertedOrderId,
        convertedAt: estimate.convertedAt,
      };
    });
  }, [estimate.convertedOrderId, estimate.convertedAt, estimate.id, showDetails?.id]);

  const loadFull = async (): Promise<Estimate> => {
    const res = await axios.get(`${API_BASE_URL}account/${id}/estimate/${estimate.id}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    const data = res.data as Estimate;
    return {
      ...data,
      products: coerceLineProducts(data?.products) as Estimate["products"],
    };
  };

  const handleDownloadPDF = async () => {
    try {
      const full = await loadFull();
      const orderLike = {
        ...full,
        id: full.id,
        quotation: true,
        createdAt: full.createdAt,
      };
      const [{ QuotationTemplate }, { downloadPDF }] = await Promise.all([
        import("../orders/QuotationTemplate"),
        import("../../../utils/pdfGenerator"),
      ]);
      await downloadPDF(
        <QuotationTemplate order={orderLike} documentTitle="QUOTATION" showQuotationMeta={false} />,
        `Quotation_${full.customerName || "Customer"}.pdf`
      );
    } catch (e) {
      console.error(e);
      alert("Failed to download PDF");
    }
  };

  const handleDownloadPrintFormatPDF = async () => {
    try {
      const full = await loadFull();
      const orderLike = {
        ...full,
        id: full.id,
        quotation: true,
        createdAt: full.createdAt,
      };
      const [{ QuotationTemplate }, { downloadPDF }] = await Promise.all([
        import("../orders/QuotationTemplate_ForPrint"),
        import("../../../utils/pdfGenerator"),
      ]);
      await downloadPDF(
        <QuotationTemplate order={orderLike} />,
        `Quotation_PrintFormat_${full.customerName || "Customer"}.pdf`
      );
    } catch (e) {
      console.error(e);
      alert("Failed to download print format PDF");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this quotation?")) return;
    try {
      await axios.delete(`${API_BASE_URL}account/${id}/estimate/${estimate.id}/delete`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      refresh();
    } catch (e: any) {
      alert(e?.response?.data?.message || "Delete failed");
    }
  };

  const advanceTotal =
    parseFloat(estimate.advance || "0") + parseFloat(estimate.advance2 || "0");

  return (
    <>
      <Card className="p-0 border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-emerald-300 transition-all group">
        <CardHeader className="bg-gradient-to-r from-emerald-50 to-green-100 flex justify-between items-start px-2.5 py-2 border-b-2 border-gray-200">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base font-bold text-gray-900 group-hover:text-emerald-700 transition-colors truncate">
              {estimate.customerName}
            </CardTitle>
            <p className="text-[11px] text-gray-500 mt-0.5 font-medium">
              Quotation ID: <span className="text-emerald-600 font-semibold">{estimate.id}</span>
              {converted && estimate.convertedOrderId ? (
                <>
                  {" · "}Order:{" "}
                  <span className="text-emerald-600 font-semibold">{estimate.convertedOrderId}</span>
                </>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span
              className={`px-2 py-0.5 rounded-full text-[11px] font-bold inline-flex items-center gap-1 ${
                converted ? "bg-slate-100 text-slate-700" : "bg-emerald-100 text-emerald-700"
              }`}
            >
              {converted && <CheckCircle className="w-3.5 h-3.5" />}
              {converted ? "Converted" : "Open"}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-white">
                  <MoreVertical className="w-4 h-4 text-gray-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white rounded-lg shadow-lg z-[100]">
                <DropdownMenuItem
                  onClick={handleDownloadPDF}
                  className="cursor-pointer hover:bg-emerald-50 text-gray-700"
                >
                  Download PDF
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDownloadPrintFormatPDF}
                  className="cursor-pointer hover:bg-emerald-50 text-gray-700"
                >
                  Download print format PDF
                </DropdownMenuItem>
                {!converted && (
                  <>
                    <DropdownMenuItem
                      onClick={() => onEdit(estimate)}
                      className="cursor-pointer hover:bg-emerald-50 text-gray-700"
                    >
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onConvert(estimate)}
                      className="cursor-pointer hover:bg-emerald-50 text-gray-700"
                    >
                      Convert to order
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDelete}
                      className="cursor-pointer hover:bg-red-50 text-red-600"
                    >
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>

        <CardContent className="px-2.5 py-2 space-y-1.5">
          <div className="grid grid-cols-2 gap-1.5">
            <div className="bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5">
              <p className="text-[10px] leading-tight text-gray-500 font-semibold mb-0.5">Created By</p>
              <p className="text-xs font-bold text-gray-900 truncate leading-snug">
                {estimate.createdBy || "N/A"}
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 min-w-0">
              <p className="text-[10px] leading-tight text-amber-700 font-semibold mb-0.5">
                Created Date & Time
              </p>
              <p className="text-xs font-bold text-gray-900 leading-snug whitespace-nowrap truncate">
                {formatIndiaDateTime(estimate.createdAt)}
              </p>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-md px-2 py-1.5">
              <p className="text-[10px] leading-tight text-teal-700 font-semibold mb-0.5">Advance</p>
              <p className="text-xs font-bold text-gray-900 leading-snug">
                ₹{formatMoney(String(advanceTotal || 0))}
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
              <p className="text-[10px] leading-tight text-slate-600 font-semibold mb-0.5">Balance</p>
              <p className="text-xs font-bold text-gray-900 leading-snug">
                ₹{formatMoney(estimate.balance)}
              </p>
            </div>
          </div>

          <div className="flex gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => setShowDetails(await loadFull())}
              className="flex-1 min-w-0 px-1.5 text-xs border-emerald-300 text-emerald-600 hover:bg-emerald-50 font-semibold"
            >
              <Info className="w-3.5 h-3.5 mr-0.5 shrink-0" /> View Details
            </Button>
            {!converted && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => onEdit(await loadFull())}
                className="flex-1 min-w-0 px-1.5 text-xs border-emerald-300 text-emerald-600 hover:bg-emerald-50 font-semibold"
              >
                <Edit className="w-3.5 h-3.5 mr-0.5 shrink-0" /> Edit
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPDF}
              className="flex-1 min-w-0 px-1.5 text-xs border-green-300 text-green-600 hover:bg-green-50 font-semibold"
            >
              <FileDown className="w-3.5 h-3.5 mr-0.5 shrink-0" /> PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {showDetails && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setShowDetails(null)}
        />
      )}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-[500px] md:h-auto max-h-screen md:max-h-[95vh] overflow-y-auto rounded-t-2xl md:rounded-lg ${
          showDetails ? "translate-y-0 md:scale-100" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        {showDetails && (
          <>
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
              <h2 className="text-xl font-bold text-gray-900">
                Quotation Details — {showDetails.customerName}
              </h2>
              <button
                type="button"
                onClick={() => setShowDetails(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ✕
              </button>
            </div>
            <div className="p-6 pb-20 md:pb-6 space-y-4">
              <div className="bg-white border-2 border-gray-300 rounded-xl p-6 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase mb-1">Quotation ID</p>
                    <p className="text-base font-bold text-gray-900 break-words">{showDetails.id}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase mb-1">Status</p>
                    <p className="text-base font-bold text-gray-900 break-words">
                      {showDetails.convertedOrderId ? "Converted" : "Open"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase mb-1">Customer Name</p>
                    <p className="text-base font-bold text-gray-900 break-words">
                      {showDetails.customerName}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase mb-1">Created By</p>
                    <p className="text-base font-bold text-gray-900 break-words">
                      {showDetails.createdBy}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-600 uppercase mb-1">Created On</p>
                    <p className="text-base font-bold text-gray-900 break-words">
                      {formatIndiaDateTime(showDetails.createdAt)}
                    </p>
                  </div>
                  {showDetails.convertedOrderId ? (
                    <div className="md:col-span-2">
                      <p className="text-xs font-bold text-gray-600 uppercase mb-1">Linked Order</p>
                      <Link
                        to="/panel/orders"
                        className="text-base font-bold text-emerald-600 hover:underline"
                      >
                        Order #{showDetails.convertedOrderId}
                      </Link>
                    </div>
                  ) : null}
                  {showDetails.siteAddress ? (
                    <div className="md:col-span-2">
                      <p className="text-xs font-bold text-gray-600 uppercase mb-1">Site Address</p>
                      <p className="text-base font-bold text-gray-900 break-words whitespace-pre-wrap">
                        {showDetails.siteAddress}
                      </p>
                    </div>
                  ) : null}
                  {showDetails.notes ? (
                    <div className="md:col-span-2">
                      <p className="text-xs font-bold text-gray-600 uppercase mb-1">Note</p>
                      <p className="text-base font-bold text-gray-900 break-words whitespace-pre-wrap">
                        {showDetails.notes}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>

              <OrderProductsDetailsSection
                products={showDetails.products}
                showMoney
                accountId={id}
                authToken={authToken}
                historyKeyPrefix={`estimate-${showDetails.id}`}
              />

              <PriceSummaryReadOnly
                price={showDetails.price}
                gst={showDetails.gst}
                discount={showDetails.discount}
                fixing={showDetails.fixing}
                transport={showDetails.transport}
                total={showDetails.total}
                advance={showDetails.advance}
                advance2={showDetails.advance2}
                balance={showDetails.balance}
                products={showDetails.products}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};
